import type { EscalationReport } from '@/report/types'
import type { RuntimeConfig } from './runtime-config'

export const ESCALATION_REPORT_TYPE = 'escalation'

/** Auth's verify attempt cap (`LinkGrantOptions.MaxVerifyAttempts`); the last refusal locks the grant. */
export const MAX_CODE_ATTEMPTS = 5

export interface ReportDescriptor {
  organizationId: string
  reportType: string
  reportDate: string
}

export interface LinkChallenge {
  factor: 'EmailOtp' | 'SmsOtp' | 'DateOfBirth' | 'None'
  maskedEmail?: string | null
}

export type ChallengeOutcome =
  | { status: 'live'; challenge: LinkChallenge }
  | { status: 'throttled' }
  | { status: 'dead' }

export interface VerifiedAccess {
  accessToken: string
  expiresInSeconds: number
  organizationId: string
  scope: Record<string, string>
}

/** Auth answers every refused verify with the same `410`, a wrong code and a locked grant alike. */
export type VerifyOutcome = { status: 'verified'; access: VerifiedAccess } | { status: 'refused' }

/** Reporting answers every token it will not accept with `404`, an expired one included. */
export type ReportOutcome = { status: 'ready'; report: EscalationReport } | { status: 'refused' }

export interface ReportSession {
  accessToken: string
  descriptor: ReportDescriptor
}

export function toReportDescriptor(access: Pick<VerifiedAccess, 'organizationId' | 'scope'>): ReportDescriptor | null {
  const reportType = access.scope.report_type
  const reportDate = access.scope.report_date
  if (!access.organizationId || !reportType || !reportDate) return null
  return { organizationId: access.organizationId, reportType, reportDate }
}

function storageKey(linkToken: string): string {
  return `laennec:report-access:${linkToken}`
}

export function storeReportSession(linkToken: string, session: ReportSession): void {
  sessionStorage.setItem(storageKey(linkToken), JSON.stringify(session))
}

export function readReportSession(linkToken: string): ReportSession | null {
  const raw = sessionStorage.getItem(storageKey(linkToken))
  if (!raw) return null
  try {
    const session = JSON.parse(raw) as Partial<ReportSession>
    return session.accessToken && session.descriptor ? (session as ReportSession) : null
  } catch {
    return null
  }
}

export function clearReportSession(linkToken: string): void {
  sessionStorage.removeItem(storageKey(linkToken))
}

async function errorMessageFrom(response: Response): Promise<string> {
  try {
    const body = (await response.clone().json()) as { error?: string; title?: string }
    return body.error ?? body.title ?? response.statusText
  } catch {
    return response.statusText
  }
}

function postJson(url: string, body: unknown): Promise<Response> {
  return fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  })
}

export async function challengeReportLink(config: RuntimeConfig, linkToken: string): Promise<ChallengeOutcome> {
  const response = await postJson(`${config.authApiBaseUrl}/v1/public/link-grants/challenge`, { token: linkToken })

  if (response.status === 410) return { status: 'dead' }
  if (response.status === 429) return { status: 'throttled' }
  if (!response.ok) throw new Error(await errorMessageFrom(response))

  return { status: 'live', challenge: (await response.json()) as LinkChallenge }
}

export async function verifyReportLinkCode(
  config: RuntimeConfig,
  linkToken: string,
  code: string,
): Promise<VerifyOutcome> {
  const response = await postJson(`${config.authApiBaseUrl}/v1/public/link-grants/verify`, {
    token: linkToken,
    value: code,
  })

  if (response.status === 410) return { status: 'refused' }
  if (!response.ok) throw new Error(await errorMessageFrom(response))

  return { status: 'verified', access: (await response.json()) as VerifiedAccess }
}

export async function fetchReport(config: RuntimeConfig, session: ReportSession): Promise<ReportOutcome> {
  const { organizationId, reportType, reportDate } = session.descriptor
  const path = [organizationId, 'reports', reportType, reportDate].map(encodeURIComponent).join('/')
  const response = await fetch(`${config.reportingApiBaseUrl}/v1/organizations/${path}`, {
    headers: { Authorization: `Bearer ${session.accessToken}`, Accept: 'application/json' },
  })

  if (response.status === 404) return { status: 'refused' }
  if (!response.ok) throw new Error(await errorMessageFrom(response))

  return { status: 'ready', report: (await response.json()) as EscalationReport }
}
